import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AdminShell } from '../../components/AdminShell';
import { supabase } from '../../lib/supabase';
import {
  Activity, RefreshCw, Loader2, Search, X, AlertTriangle, Mail, CreditCard,
  Bell, ShieldCheck, ServerCrash, CheckCircle2, ChevronLeft, ChevronRight,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// -----------------------------------------------------------------
// Shared helpers
// -----------------------------------------------------------------

const useAuthedFetch = () => {
  return useCallback(async (path, params = {}, opts = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    });
    const url = `${BACKEND}${path}${qs.toString() ? `?${qs}` : ''}`;
    const res = await fetch(url, {
      method: opts.method || 'GET',
      headers: {
        Authorization: `Bearer ${session?.access_token}`,
        ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.detail || `HTTP ${res.status}`);
    return body;
  }, []);
};

const fmtRel = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};
const fmtExact = (iso) => iso ? new Date(iso).toLocaleString('en-GB') : '—';

const Light = ({ color }) => (
  <span style={{
    display: 'inline-block', width: 10, height: 10, borderRadius: 999,
    background: color === 'green' ? '#10b981' : color === 'amber' ? '#f59e0b' : '#ef4444',
    boxShadow: `0 0 0 3px ${color === 'green' ? 'rgba(16,185,129,.14)' : color === 'amber' ? 'rgba(245,158,11,.14)' : 'rgba(239,68,68,.14)'}`,
    marginRight: 8, verticalAlign: 'middle',
  }} />
);

const SevChip = ({ sev }) => {
  const palette = {
    info:     { bg: '#e0e7ff', fg: '#3730a3' },
    warning:  { bg: '#fef3c7', fg: '#92400e' },
    error:    { bg: '#fee2e2', fg: '#991b1b' },
    critical: { bg: '#fecaca', fg: '#7f1d1d' },
  };
  const p = palette[sev] || palette.info;
  return (
    <span style={{
      background: p.bg, color: p.fg, padding: '2px 8px', borderRadius: 999,
      fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3,
    }}>{sev || 'info'}</span>
  );
};

const Pager = ({ offset, limit, total, onPage }) => {
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end', padding: '10px 2px' }}>
      <span style={{ color: '#64748b', fontSize: 13 }}>
        {total === 0 ? '0 rows' : `${offset + 1}–${Math.min(offset + limit, total)} of ${total}`}
      </span>
      <button onClick={() => onPage(Math.max(0, offset - limit))}
              disabled={offset === 0}
              style={pagerBtnStyle(offset === 0)}
              data-testid="sh-pager-prev">
        <ChevronLeft size={14} />
      </button>
      <span style={{ fontSize: 13 }}>{page}/{pages}</span>
      <button onClick={() => onPage(offset + limit)}
              disabled={offset + limit >= total}
              style={pagerBtnStyle(offset + limit >= total)}
              data-testid="sh-pager-next">
        <ChevronRight size={14} />
      </button>
    </div>
  );
};
const pagerBtnStyle = (dis) => ({
  background: dis ? '#f1f5f9' : '#fff', color: dis ? '#94a3b8' : '#334155',
  border: '1px solid #e2e8f0', borderRadius: 8, padding: '5px 8px',
  cursor: dis ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center',
});

// -----------------------------------------------------------------
// Health strip (shared)
// -----------------------------------------------------------------

const HealthStrip = ({ hoursBack, setHoursBack, refreshKey, onRefresh }) => {
  const authedFetch = useAuthedFetch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authedFetch('/api/admin/system-health/summary', { hours_back: hoursBack });
      setData(r);
    } catch (_) {} finally { setLoading(false); }
  }, [authedFetch, hoursBack]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div style={stripShellStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
        <select value={hoursBack} onChange={e => setHoursBack(parseInt(e.target.value))}
                data-testid="sh-window-select"
                style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}>
          <option value={1}>Last 1 hour</option>
          <option value={6}>Last 6 hours</option>
          <option value={24}>Last 24 hours</option>
          <option value={168}>Last 7 days</option>
          <option value={720}>Last 30 days</option>
        </select>
        <button onClick={onRefresh} disabled={loading} style={refreshBtnStyle}
                data-testid="sh-refresh">
          {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>
      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
        <StripCell icon={<ServerCrash size={14} />} light={data?.api?.light} label="API"
                   value={data ? `${data.api.errors_total} errs` : '—'} />
        <StripCell icon={<AlertTriangle size={14} />} light={data?.payments?.light}
                   label="Payments"
                   value={data ? `${data.payments.ok}/${data.payments.webhooks} ok` : '—'} />
        <StripCell icon={<Mail size={14} />} light={data?.emails?.light} label="Emails"
                   value={data ? `${data.emails.ok}/${data.emails.total}` : '—'} />
        <StripCell icon={<Bell size={14} />} light={(data?.incidents_open || []).length ? 'red' : 'green'}
                   label="Incidents open"
                   value={(data?.incidents_open || []).length} />
      </div>
    </div>
  );
};
const stripShellStyle = {
  display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap',
  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
  padding: '14px 18px', marginBottom: 18,
};
const refreshBtnStyle = {
  display: 'inline-flex', gap: 6, alignItems: 'center',
  padding: '7px 12px', background: '#fff', border: '1px solid #e2e8f0',
  borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#334155',
};
const StripCell = ({ icon, light, label, value }) => (
  <div>
    <div style={{ fontSize: 10.5, color: '#64748b', letterSpacing: 0.5, textTransform: 'uppercase', fontWeight: 600 }}>
      {icon} {label}
    </div>
    <div style={{ fontSize: 15, fontWeight: 600, marginTop: 2 }}>
      <Light color={light || 'green'} />{value}
    </div>
  </div>
);

// -----------------------------------------------------------------
// Tabs: Overview | Timeline | Errors | Payments | Emails
// -----------------------------------------------------------------

const TAB_LIST = [
  { key: 'overview',  label: 'Overview'  },
  { key: 'timeline',  label: 'Timeline'  },
  { key: 'errors',    label: 'Errors'    },
  { key: 'payments',  label: 'Payments'  },
  { key: 'emails',    label: 'Emails'    },
];

// ---------------- Overview --------------------------------------
const OverviewTab = ({ hoursBack, refreshKey }) => {
  const authedFetch = useAuthedFetch();
  const [summary, setSummary] = useState(null);
  const [recentErrors, setRecentErrors] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const s = await authedFetch('/api/admin/system-health/summary', { hours_back: hoursBack });
        setSummary(s);
        const e = await authedFetch('/api/admin/system-health/errors', { hours_back: hoursBack, limit: 5 });
        setRecentErrors(e.rows || []);
      } catch (_) {}
    })();
  }, [authedFetch, hoursBack, refreshKey]);
  if (!summary) return <div style={{ padding: 30, color: '#64748b' }}>Loading overview…</div>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
      <Card title="Open incidents" icon={<Bell size={14} />}>
        {(summary.incidents_open || []).length === 0 ? (
          <Empty icon={<CheckCircle2 size={28} color="#10b981" />}
                 title="All green" sub="No incidents open — the platform is behaving." />
        ) : (
          <ul style={listStyle} data-testid="sh-incidents-list">
            {summary.incidents_open.map(i => (
              <li key={i.id} style={listItemStyle}>
                <SevChip sev={i.severity} />
                <div style={{ flex: 1, marginLeft: 10 }}>
                  <div style={{ fontWeight: 600 }}>{i.title}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    rule <code>{i.rule_key}</code> · opened {fmtRel(i.opened_at)} · metric={i.metric_value ?? '—'}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="Recent error groups" icon={<AlertTriangle size={14} />}>
        {recentErrors.length === 0 ? (
          <Empty icon={<CheckCircle2 size={28} color="#10b981" />}
                 title="No API errors" sub="Nothing failed in the selected window." />
        ) : (
          <ul style={listStyle}>
            {recentErrors.slice(0, 5).map(g => (
              <li key={g.signature} style={listItemStyle}>
                <SevChip sev={g.severity} />
                <div style={{ flex: 1, marginLeft: 10, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {g.http_status} {g.path}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    {g.exception_class} · <b>{g.count}×</b> · {g.shops_affected} shops · last {fmtRel(g.last_seen)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="API health" icon={<ServerCrash size={14} />}>
        <StatRow label="5xx + unhandled" value={summary.api.errors_total} />
        <StatRow label="Frontend crashes"  value={summary.api.frontend_errors} />
        <StatRow label="Critical"          value={summary.api.by_severity?.critical || 0} />
        <StatRow label="Warnings"          value={summary.api.by_severity?.warning  || 0} />
      </Card>
      <Card title="Delivery health" icon={<Mail size={14} />}>
        <StatRow label="Emails sent"       value={`${summary.emails.ok}/${summary.emails.total}`} />
        <StatRow label="Email failures"    value={summary.emails.failed} />
        <StatRow label="Webhook received"  value={summary.payments.webhooks} />
        <StatRow label="Webhook bad sig"   value={summary.payments.bad_sig} />
      </Card>
    </div>
  );
};
const Card = ({ title, icon, children }) => (
  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 10 }}>
      {icon}{title}
    </div>
    {children}
  </div>
);
const Empty = ({ icon, title, sub }) => (
  <div style={{ textAlign: 'center', padding: 20 }}>
    {icon}
    <div style={{ fontWeight: 600, marginTop: 8 }}>{title}</div>
    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{sub}</div>
  </div>
);
const StatRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px dashed #f1f5f9' }}>
    <span style={{ color: '#64748b', fontSize: 13 }}>{label}</span>
    <span style={{ fontWeight: 600 }}>{value}</span>
  </div>
);
const listStyle = { listStyle: 'none', margin: 0, padding: 0 };
const listItemStyle = {
  display: 'flex', alignItems: 'center', padding: '10px 0',
  borderBottom: '1px dashed #f1f5f9',
};

// ---------------- Timeline --------------------------------------
const TimelineTab = ({ hoursBack, refreshKey }) => {
  const authedFetch = useAuthedFetch();
  const [severity, setSeverity] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ rows: [], total: 0 });
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authedFetch('/api/admin/system-health/timeline', {
        hours_back: hoursBack, severity, category, search, limit, offset,
      });
      setData(r);
    } catch (_) {} finally { setLoading(false); }
  }, [authedFetch, hoursBack, severity, category, search, offset]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setOffset(0); }, [severity, category, search, hoursBack]);

  return (
    <div>
      <Filters>
        <Select value={severity} onChange={setSeverity} testid="sh-tl-sev"
                options={[{v:'',l:'All severities'},{v:'info',l:'Info'},{v:'warning',l:'Warning'},{v:'error',l:'Error'},{v:'critical',l:'Critical'}]} />
        <Select value={category} onChange={setCategory} testid="sh-tl-cat"
                options={[{v:'',l:'All categories'},{v:'api',l:'API'},{v:'db',l:'Database'},{v:'stripe',l:'Stripe'},{v:'resend',l:'Resend'},{v:'frontend',l:'Frontend'},{v:'incident',l:'Incident'}]} />
        <SearchBox value={search} onChange={setSearch} placeholder="Search message, path, UA…" />
      </Filters>
      <Table data-testid="sh-tl-table">
        <thead><tr>
          <Th>When</Th><Th>Severity</Th><Th>Category</Th><Th>Title</Th><Th>Detail</Th>
        </tr></thead>
        <tbody>
          {loading && <tr><td colSpan={5} style={cellStyle}>Loading…</td></tr>}
          {!loading && data.rows.length === 0 && (
            <tr><td colSpan={5} style={{ ...cellStyle, textAlign: 'center', color: '#64748b', padding: 24 }}>
              Nothing in the timeline for the selected filters.
            </td></tr>
          )}
          {data.rows.map((r, i) => (
            <tr key={r.id + ':' + r.kind + ':' + i} style={{ borderTop: '1px solid #f1f5f9' }}>
              <Td title={fmtExact(r.ts)}>{fmtRel(r.ts)}</Td>
              <Td><SevChip sev={r.severity} /></Td>
              <Td><code style={{ fontSize: 12 }}>{r.category}</code></Td>
              <Td style={{ fontWeight: 500 }}>{r.title}</Td>
              <Td style={{ color: '#475569', fontSize: 12.5, maxWidth: 450, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={r.detail}>{r.detail}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Pager offset={offset} limit={limit} total={data.total} onPage={setOffset} />
    </div>
  );
};

// ---------------- Errors ----------------------------------------
const ErrorsTab = ({ hoursBack, refreshKey }) => {
  const authedFetch = useAuthedFetch();
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('');
  const [httpStatus, setHttpStatus] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ rows: [], total: 0 });
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authedFetch('/api/admin/system-health/errors', {
        hours_back: hoursBack, category, severity, http_status: httpStatus, search, limit, offset,
      });
      setData(r);
    } catch (_) {} finally { setLoading(false); }
  }, [authedFetch, hoursBack, category, severity, httpStatus, search, offset]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setOffset(0); }, [category, severity, httpStatus, search, hoursBack]);

  return (
    <div>
      <Filters>
        <Select value={category} onChange={setCategory} testid="sh-err-cat"
                options={[{v:'',l:'All categories'},{v:'api',l:'API'},{v:'db',l:'Database'},{v:'stripe',l:'Stripe'},{v:'resend',l:'Resend'},{v:'auth',l:'Auth'},{v:'webhook',l:'Webhook'},{v:'other',l:'Other'}]} />
        <Select value={severity} onChange={setSeverity} testid="sh-err-sev"
                options={[{v:'',l:'All severities'},{v:'warning',l:'Warning'},{v:'error',l:'Error'},{v:'critical',l:'Critical'}]} />
        <Select value={httpStatus} onChange={setHttpStatus} testid="sh-err-http"
                options={[{v:'',l:'Any status'},{v:'500',l:'500'},{v:'502',l:'502'},{v:'503',l:'503'},{v:'504',l:'504'}]} />
        <SearchBox value={search} onChange={setSearch} placeholder="Search exception / path / message…" />
      </Filters>
      <Table data-testid="sh-err-table">
        <thead><tr>
          <Th>Count</Th><Th>Severity</Th><Th>Exception · Path</Th><Th>Status</Th>
          <Th>Shops</Th><Th>Users</Th><Th>First seen</Th><Th>Last seen</Th>
        </tr></thead>
        <tbody>
          {loading && <tr><td colSpan={8} style={cellStyle}>Loading…</td></tr>}
          {!loading && data.rows.length === 0 && (
            <tr><td colSpan={8} style={{ ...cellStyle, textAlign: 'center', color: '#64748b', padding: 24 }}>
              No errors in the selected window — nice.
            </td></tr>
          )}
          {data.rows.map(g => (
            <tr key={g.signature} style={{ borderTop: '1px solid #f1f5f9' }}>
              <Td style={{ fontWeight: 700 }}>{g.count}</Td>
              <Td><SevChip sev={g.severity} /></Td>
              <Td style={{ fontFamily: 'monospace', fontSize: 12.5, maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={`${g.exception_class} on ${g.path}`}>
                <b>{g.exception_class}</b> · {g.path}
              </Td>
              <Td>{g.http_status}</Td>
              <Td>{g.shops_affected}</Td>
              <Td>{g.users_affected}</Td>
              <Td title={fmtExact(g.first_seen)}>{fmtRel(g.first_seen)}</Td>
              <Td title={fmtExact(g.last_seen)}>{fmtRel(g.last_seen)}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Pager offset={offset} limit={limit} total={data.total} onPage={setOffset} />
    </div>
  );
};

// ---------------- Payments (Stripe webhooks) --------------------
const PaymentsTab = ({ hoursBack, refreshKey }) => {
  const authedFetch = useAuthedFetch();
  const [outcome, setOutcome] = useState('');
  const [eventType, setEventType] = useState('');
  const [sigValid, setSigValid] = useState('');
  const [data, setData] = useState({ rows: [], total: 0 });
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 50;
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authedFetch('/api/admin/system-health/stripe-webhooks', {
        hours_back: hoursBack, outcome, event_type: eventType,
        signature_valid: sigValid === '' ? undefined : sigValid, limit, offset,
      });
      setData(r);
    } catch (_) {} finally { setLoading(false); }
  }, [authedFetch, hoursBack, outcome, eventType, sigValid, offset]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setOffset(0); }, [outcome, eventType, sigValid, hoursBack]);

  return (
    <div>
      <Filters>
        <Select value={outcome} onChange={setOutcome} testid="sh-pay-outcome"
                options={[{v:'',l:'All outcomes'},{v:'ok',l:'OK'},{v:'skipped',l:'Skipped'},{v:'error',l:'Error'}]} />
        <Select value={eventType} onChange={setEventType} testid="sh-pay-type"
                options={[{v:'',l:'All event types'},{v:'payment_intent.succeeded',l:'payment_intent.succeeded'},{v:'payment_intent.payment_failed',l:'payment_intent.payment_failed'},{v:'charge.refunded',l:'charge.refunded'},{v:'invoice.payment_failed',l:'invoice.payment_failed'},{v:'account.updated',l:'account.updated'}]} />
        <Select value={sigValid} onChange={setSigValid} testid="sh-pay-sig"
                options={[{v:'',l:'Any signature'},{v:'true',l:'Signature OK'},{v:'false',l:'Signature failed'}]} />
      </Filters>
      <Table data-testid="sh-pay-table">
        <thead><tr>
          <Th>Received</Th><Th>Event</Th><Th>Outcome</Th><Th>Signature</Th>
          <Th>Account</Th><Th>Booking</Th><Th>Error</Th>
        </tr></thead>
        <tbody>
          {loading && <tr><td colSpan={7} style={cellStyle}>Loading…</td></tr>}
          {!loading && data.rows.length === 0 && (
            <tr><td colSpan={7} style={{ ...cellStyle, textAlign: 'center', color: '#64748b', padding: 24 }}>
              No webhook arrivals in the selected window.
            </td></tr>
          )}
          {data.rows.map(r => (
            <tr key={r.id} style={{ borderTop: '1px solid #f1f5f9' }}>
              <Td title={fmtExact(r.received_at)}>{fmtRel(r.received_at)}</Td>
              <Td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.event_type || '—'}</Td>
              <Td>
                {r.outcome === 'ok' && <span style={{ color: '#065f46', fontWeight: 600 }}>ok</span>}
                {r.outcome === 'error' && <span style={{ color: '#991b1b', fontWeight: 600 }}>error</span>}
                {r.outcome === 'skipped' && <span style={{ color: '#64748b' }}>skipped</span>}
                {!r.outcome && <span style={{ color: '#94a3b8' }}>—</span>}
              </Td>
              <Td>{r.signature_valid
                ? <ShieldCheck size={14} color="#059669" />
                : <span style={{ color: '#991b1b', fontWeight: 600 }}>FAIL</span>}
              </Td>
              <Td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.connected_account_id || '—'}</Td>
              <Td style={{ fontFamily: 'monospace', fontSize: 11 }}>
                {r.booking_id ? r.booking_id.slice(0, 8) + '…' : '—'}
              </Td>
              <Td style={{ color: '#991b1b', fontSize: 12, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={r.error_detail || ''}>{r.error_detail || '—'}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Pager offset={offset} limit={limit} total={data.total} onPage={setOffset} />
    </div>
  );
};

// ---------------- Emails ----------------------------------------
const EmailsTab = ({ hoursBack, refreshKey }) => {
  const authedFetch = useAuthedFetch();
  const [status, setStatus] = useState('');
  const [tpl, setTpl] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ rows: [], total: 0, template_stats: [] });
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 50;
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authedFetch('/api/admin/system-health/emails', {
        hours_back: hoursBack, status, template_key: tpl, search, limit, offset,
      });
      setData(r);
    } catch (_) {} finally { setLoading(false); }
  }, [authedFetch, hoursBack, status, tpl, search, offset]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setOffset(0); }, [status, tpl, search, hoursBack]);

  const tplOptions = useMemo(() => {
    const base = [{v: '', l: 'All templates'}];
    const seen = new Set((data.template_stats || []).map(t => t.template_key));
    seen.forEach(k => base.push({v: k, l: k}));
    return base;
  }, [data.template_stats]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        {(data.template_stats || []).slice(0, 6).map(s => (
          <div key={s.template_key} style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 12px',
            minWidth: 150, fontSize: 12.5,
          }}>
            <div style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
              {s.template_key}
            </div>
            <div style={{ marginTop: 2, fontWeight: 600 }}>
              {s.ok}/{s.total} ok
              {s.failed > 0 && <span style={{ color: '#991b1b', marginLeft: 8 }}>· {s.failed} failed</span>}
              {s.bounced > 0 && <span style={{ color: '#92400e', marginLeft: 8 }}>· {s.bounced} bounced</span>}
            </div>
          </div>
        ))}
      </div>
      <Filters>
        <Select value={status} onChange={setStatus} testid="sh-email-status"
                options={[{v:'',l:'All statuses'},{v:'sent',l:'Sent'},{v:'delivered',l:'Delivered'},{v:'failed',l:'Failed'},{v:'bounced',l:'Bounced'},{v:'complained',l:'Complained'},{v:'skipped',l:'Skipped'}]} />
        <Select value={tpl} onChange={setTpl} testid="sh-email-tpl" options={tplOptions} />
        <SearchBox value={search} onChange={setSearch} placeholder="Search recipient, subject, error…" />
      </Filters>
      <Table data-testid="sh-email-table">
        <thead><tr>
          <Th>When</Th><Th>Status</Th><Th>Template</Th><Th>To</Th><Th>Subject</Th>
          <Th>Latency</Th><Th>Error</Th>
        </tr></thead>
        <tbody>
          {loading && <tr><td colSpan={7} style={cellStyle}>Loading…</td></tr>}
          {!loading && data.rows.length === 0 && (
            <tr><td colSpan={7} style={{ ...cellStyle, textAlign: 'center', color: '#64748b', padding: 24 }}>
              No email attempts in the selected window.
            </td></tr>
          )}
          {data.rows.map(r => (
            <tr key={r.id} style={{ borderTop: '1px solid #f1f5f9' }}>
              <Td title={fmtExact(r.created_at)}>{fmtRel(r.created_at)}</Td>
              <Td>
                {['sent','delivered'].includes(r.status) && <span style={{ color: '#065f46', fontWeight: 600 }}>{r.status}</span>}
                {['failed','bounced','complained'].includes(r.status) && <SevChip sev="error" />}
                {r.status === 'skipped' && <span style={{ color: '#64748b' }}>skipped</span>}
              </Td>
              <Td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.template_key || '—'}</Td>
              <Td style={{ fontSize: 12.5 }}>{r.to_email}</Td>
              <Td style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={r.subject || ''}>{r.subject || '—'}</Td>
              <Td>{r.latency_ms != null ? `${r.latency_ms}ms` : '—'}</Td>
              <Td style={{ color: '#991b1b', fontSize: 12, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={r.error_detail || ''}>{r.error_detail || '—'}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Pager offset={offset} limit={limit} total={data.total} onPage={setOffset} />
    </div>
  );
};

// -----------------------------------------------------------------
// Small shared UI atoms
// -----------------------------------------------------------------

const Filters = ({ children }) => (
  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>{children}</div>
);
const Select = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={e => onChange(e.target.value)}
          data-testid={testid}
          style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff' }}>
    {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
  </select>
);
const SearchBox = ({ value, onChange, placeholder }) => (
  <div style={{ position: 'relative', flex: 1, minWidth: 260 }}>
    <Search size={14} style={{ position: 'absolute', top: 10, left: 10, color: '#94a3b8' }} />
    <input value={value} onChange={e => onChange(e.target.value)}
           placeholder={placeholder}
           data-testid="sh-search"
           style={{ width: '100%', padding: '7px 10px 7px 30px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
    {value && <X size={14} onClick={() => onChange('')} style={{ position: 'absolute', top: 10, right: 8, color: '#94a3b8', cursor: 'pointer' }} />}
  </div>
);
const Table = (props) => (
  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} {...props} />
  </div>
);
const Th = ({ children }) => (
  <th style={{ textAlign: 'left', padding: '10px 14px', background: '#f8fafc',
               fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4,
               fontWeight: 700, borderBottom: '1px solid #e2e8f0' }}>{children}</th>
);
const Td = ({ children, style, ...rest }) => (
  <td style={{ padding: '10px 14px', ...style }} {...rest}>{children}</td>
);
const cellStyle = { padding: 14 };


// -----------------------------------------------------------------
// Page shell
// -----------------------------------------------------------------

const SystemHealth = () => {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'overview';
  const [hoursBack, setHoursBack] = useState(24);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey(k => k + 1);

  const setTab = (k) => {
    const next = new URLSearchParams(params);
    next.set('tab', k);
    setParams(next, { replace: true });
  };

  return (
    <AdminShell>
      <div style={{ padding: '26px 28px 60px', maxWidth: 1400, margin: '0 auto' }} data-testid="system-health-page">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <Activity size={18} color="#334155" />
          <h2 style={{ margin: 0, fontSize: 20 }}>System health</h2>
        </div>
        <p style={{ color: '#64748b', fontSize: 13.5, margin: '2px 0 20px' }}>
          Everything the platform did, failed to do, or almost failed to do — API errors, email delivery, Stripe webhooks, incidents.
        </p>

        <HealthStrip hoursBack={hoursBack} setHoursBack={setHoursBack}
                     refreshKey={refreshKey} onRefresh={refresh} />

        <div style={{ display: 'flex', gap: 2, marginBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
          {TAB_LIST.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
                    data-testid={`sh-tab-${t.key}`}
                    style={{
                      padding: '10px 16px', background: 'transparent',
                      border: 'none', borderBottom: tab === t.key ? '2px solid #4f46ff' : '2px solid transparent',
                      color: tab === t.key ? '#1e1b4b' : '#64748b',
                      fontWeight: tab === t.key ? 600 : 500,
                      cursor: 'pointer', fontSize: 14,
                    }}>{t.label}</button>
          ))}
        </div>

        {tab === 'overview' && <OverviewTab hoursBack={hoursBack} refreshKey={refreshKey} />}
        {tab === 'timeline' && <TimelineTab hoursBack={hoursBack} refreshKey={refreshKey} />}
        {tab === 'errors'   && <ErrorsTab   hoursBack={hoursBack} refreshKey={refreshKey} />}
        {tab === 'payments' && <PaymentsTab hoursBack={hoursBack} refreshKey={refreshKey} />}
        {tab === 'emails'   && <EmailsTab   hoursBack={hoursBack} refreshKey={refreshKey} />}
      </div>
    </AdminShell>
  );
};

export default SystemHealth;
